import React, { useState, useEffect } from 'react';
import ciudadService from '../services/ciudadService';
import { toast } from 'react-toastify';

const Ciudades = () => {
  const [ciudades, setCiudades] = useState([]);
  const [nuevaCiudad, setNuevaCiudad] = useState('');
  const [ciudadEditando, setCiudadEditando] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar ciudades al montar el componente
  useEffect(() => {
    fetchCiudades();
  }, []);

  // Función para obtener todas las ciudades
  const fetchCiudades = async () => {
    try {
      setLoading(true);
      const data = await ciudadService.getCiudades();
      console.log('Ciudades obtenidas:', data);
      setCiudades(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error('Error al cargar ciudades:', err);
      setError('No se pudieron cargar las ciudades. Por favor, intenta de nuevo más tarde.');
      toast.error('Error al cargar ciudades');
    } finally {
      setLoading(false);
    }
  };

  // Función para agregar una nueva ciudad
  const handleAddCiudad = async (e) => {
    e.preventDefault();
    if (!nuevaCiudad.trim()) {
      toast.warning('Por favor ingresa un nombre de ciudad');
      return;
    }

    try {
      setLoading(true);
      // Enviar solo el nombre, el servicio se encarga de agregar los valores por defecto
      const ciudadCreada = await ciudadService.createCiudad(nuevaCiudad.trim());
      setCiudades([...ciudades, ciudadCreada]);
      setNuevaCiudad('');
      toast.success('Ciudad agregada exitosamente');
    } catch (err) {
      console.error('Error al agregar ciudad:', err);
      toast.error('Error al agregar ciudad');
    } finally {
      setLoading(false);
    }
  };

  // Función para iniciar la edición de una ciudad
  const handleEditInit = (ciudad) => {
    setCiudadEditando({
      id: ciudad.id,
      name: ciudad.name
    });
  };

  // Función para cancelar la edición
  const handleCancelEdit = () => {
    setCiudadEditando(null);
  };

  // Función para guardar los cambios de una ciudad editada
  const handleSaveEdit = async () => {
    if (!ciudadEditando || !ciudadEditando.name.trim()) {
      toast.warning('El nombre de la ciudad no puede estar vacío');
      return;
    }

    try {
      setLoading(true);
      // Buscar la ciudad original para mantener sus propiedades
      const ciudadOriginal = ciudades.find(c => c.id === ciudadEditando.id);
      
      // Mantener los valores originales excepto el nombre que se está editando
      const ciudadData = {
        name: ciudadEditando.name.trim(),
        state: ciudadOriginal.state || 'Antioquia',
        country: ciudadOriginal.country || 'Colombia',
        isActive: ciudadOriginal.isActive !== undefined ? ciudadOriginal.isActive : true,
        iconUrl: ciudadOriginal.iconUrl || ''
      };
      
      const ciudadActualizada = await ciudadService.updateCiudad(ciudadEditando.id, ciudadData);
      
      setCiudades(ciudades.map(ciudad => 
        ciudad.id === ciudadEditando.id ? ciudadActualizada : ciudad
      ));
      
      setCiudadEditando(null);
      toast.success('Ciudad actualizada exitosamente');
    } catch (err) {
      console.error('Error al actualizar ciudad:', err);
      toast.error('Error al actualizar ciudad');
    } finally {
      setLoading(false);
    }
  };

  // Función para eliminar una ciudad
  const handleDeleteCiudad = async (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta ciudad?')) {
      try {
        setLoading(true);
        await ciudadService.deleteCiudad(id);
        setCiudades(ciudades.filter(ciudad => ciudad.id !== id));
        toast.success('Ciudad eliminada exitosamente');
      } catch (err) {
        console.error('Error al eliminar ciudad:', err);
        toast.error('Error al eliminar ciudad');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Gestión de Ciudades</h2>
      <div className="bg-white rounded-lg shadow-md p-6">
        <p className="text-gray-600 mb-4">
          Administra las ciudades donde opera Xervix.
        </p>
        
        {/* Formulario para agregar ciudad */}
        <form onSubmit={handleAddCiudad} className="mb-8">
          <div className="flex items-center">
            <input
              type="text"
              value={nuevaCiudad}
              onChange={(e) => setNuevaCiudad(e.target.value)}
              placeholder="Nombre de la ciudad"
              className="flex-grow shadow appearance-none border rounded py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
            <button
              type="submit"
              disabled={loading}
              className="ml-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              {loading ? 'Agregando...' : 'Agregar Ciudad'}
            </button>
          </div>
        </form>

        {/* Estado de carga y error */}
        {loading && <p className="mt-4 text-gray-600">Cargando ciudades...</p>}
        {error && <p className="mt-4 text-red-500">{error}</p>}
        
        {/* Tabla de ciudades */}
        {!loading && !error && (
          <div className="overflow-x-auto mt-6">
            <table className="min-w-full bg-white">
              <thead>
                <tr className="w-full h-16 border-gray-300 border-b py-8">
                  <th className="text-left pl-6 font-bold text-gray-600">Nombre</th>
                  <th className="text-left font-bold text-gray-600">Estado/Provincia</th>
                  <th className="text-left font-bold text-gray-600">País</th>
                  <th className="text-center font-bold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ciudades.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-6 text-gray-500">
                      No hay ciudades registradas. ¡Agrega la primera!
                    </td>
                  </tr>
                ) : (
                  ciudades.map(ciudad => (
                    <tr key={ciudad.id} className="h-16 border-gray-300 border-b hover:bg-gray-50">
                      <td className="py-3 px-6 text-left">
                        {ciudadEditando && ciudadEditando.id === ciudad.id ? (
                          <input
                            type="text"
                            value={ciudadEditando.name}
                            onChange={(e) => setCiudadEditando({...ciudadEditando, name: e.target.value})}
                            className="shadow appearance-none border rounded py-1 px-2 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                          />
                        ) : (
                          ciudad.name
                        )}
                      </td>
                      <td className="py-3 px-6 text-left">{ciudad.state || 'Antioquia'}</td>
                      <td className="py-3 px-6 text-left">{ciudad.country || 'Colombia'}</td>
                      <td className="py-3 px-6 text-center">
                        <div className="flex item-center justify-center">
                          {ciudadEditando && ciudadEditando.id === ciudad.id ? (
                            <>
                              <button
                                onClick={handleCancelEdit}
                                className="text-gray-500 hover:text-gray-700 mx-1"
                              >
                                Cancelar
                              </button>
                              <button
                                onClick={handleSaveEdit}
                                className="text-green-500 hover:text-green-700 mx-1"
                              >
                                Guardar
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditInit(ciudad)}
                                className="text-blue-500 hover:text-blue-700 mx-1"
                              >
                                Editar
                              </button>
                              <button
                                onClick={() => handleDeleteCiudad(ciudad.id)}
                                className="text-red-500 hover:text-red-700 mx-1"
                              >
                                Eliminar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Ciudades;
